import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import ImageUploader from '../../components/ImageUploader';
import { Plus, Pencil, Trash2, Calendar, MapPin, Clock, RefreshCw, Image } from 'lucide-react';

const AdminEvents = () => {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'meeting',
    date: '',
    time: '',
    location: '',
    image_url: ''
  });

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleSubmit = async () => {
    if (!formData.title || !formData.date || !formData.time || !formData.location) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editing) {
        await axios.put(`${API}/events/${editing}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Event updated!');
      } else {
        await axios.post(`${API}/events`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Event created!');
      }
      setShowDialog(false);
      setFormData({ title: '', description: '', event_type: 'meeting', date: '', time: '', location: '', image_url: '' });
      setEditing(null);
      fetchEvents();
    } catch (error) {
      toast.error('Failed to save event');
    }
  };

  const handleEdit = (item) => {
    setFormData({
      title: item.title,
      description: item.description,
      event_type: item.event_type,
      date: item.date,
      time: item.time,
      location: item.location,
      image_url: item.image_url || ''
    });
    setEditing(item.id);
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API}/events/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Event deleted!');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const typeColors = { 
    meeting: 'bg-blue-500', 
    canvassing: 'bg-emerald-500', 
    cleanup: 'bg-amber-500',
    rally: 'bg-purple-500',
    townhall: 'bg-red-500',
    other: 'bg-slate-500'
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-plp-blue" />
            Events
          </h1>
          <p className="text-slate-600">Manage campaign events and activities</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) { setEditing(null); setFormData({ title: '', description: '', event_type: 'meeting', date: '', time: '', location: '', image_url: '' }); }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-plp-blue hover:bg-plp-blue-light" data-testid="add-event-btn">
              <Plus className="w-4 h-4 mr-2" /> New Event
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? 'Edit Event' : 'New Event'}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Event Type</Label>
                <Select value={formData.event_type} onValueChange={(v) => setFormData(p => ({ ...p, event_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="meeting">Meet & Greet</SelectItem>
                    <SelectItem value="canvassing">Canvassing</SelectItem>
                    <SelectItem value="cleanup">Community Cleanup</SelectItem>
                    <SelectItem value="rally">Rally</SelectItem>
                    <SelectItem value="townhall">Town Hall</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Title *</Label>
                <Input 
                  value={formData.title} 
                  onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))} 
                  placeholder="Event title" 
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea 
                  value={formData.description} 
                  onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} 
                  rows={3} 
                  placeholder="Event description..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Input type="date" value={formData.date} onChange={(e) => setFormData(p => ({ ...p, date: e.target.value }))} />
                </div>
                <div>
                  <Label>Time *</Label>
                  <Input type="time" value={formData.time} onChange={(e) => setFormData(p => ({ ...p, time: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label>Location *</Label>
                <Input 
                  value={formData.location} 
                  onChange={(e) => setFormData(p => ({ ...p, location: e.target.value }))} 
                  placeholder="Event location" 
                />
              </div>
              
              {/* Image Upload Section */}
              <ImageUploader
                label="Event Flyer / Image"
                value={formData.image_url}
                onChange={(url) => setFormData(p => ({ ...p, image_url: url }))}
                placeholder="https://example.com/flyer.jpg"
              />
              
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowDialog(false)} className="flex-1">Cancel</Button>
                <Button onClick={handleSubmit} className="flex-1 bg-plp-blue">{editing ? 'Update' : 'Create'}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-plp-blue" /></div>
      ) : events.length === 0 ? (
        <Card className="bg-white"><CardContent className="py-12 text-center"><Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">No events yet</p></CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((item) => (
            <Card key={item.id} className="bg-white overflow-hidden">
              {item.image_url && (
                <div className="relative h-40 bg-slate-100">
                  <img 
                    src={item.image_url.startsWith('/api') ? `${window.location.origin.replace(':3000', ':8001')}${item.image_url}` : item.image_url}
                    alt={item.title}
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  <Badge className={`absolute top-2 left-2 ${typeColors[item.event_type] || typeColors.other} text-white capitalize`}>
                    {item.event_type}
                  </Badge>
                </div>
              )}
              <CardContent className={`p-4 ${!item.image_url ? 'pt-4' : ''}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {!item.image_url && (
                      <Badge className={`${typeColors[item.event_type] || typeColors.other} text-white mb-2 capitalize`}>
                        {item.event_type}
                      </Badge>
                    )}
                    <h3 className="font-heading font-bold text-lg">{item.title}</h3>
                    {item.description && (
                      <p className="text-sm text-slate-500 mt-1 line-clamp-2">{item.description}</p>
                    )}
                    <div className="mt-3 space-y-1 text-sm text-slate-600">
                      <p className="flex items-center gap-2"><Calendar className="w-4 h-4 text-plp-blue" />{item.date}</p>
                      <p className="flex items-center gap-2"><Clock className="w-4 h-4 text-plp-blue" />{item.time}</p>
                      <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-plp-blue" />{item.location}</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-4 pt-3 border-t">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(item)} className="flex-1">
                    <Pencil className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminEvents;
